/**
 * MAX Mini App platform bridge (official WebApp SDK + browser fallback).
 * @see https://dev.max.ru/docs/webapps/bridge
 */

import type { MaxWebApp, MaxWebAppInitDataUnsafe, MaxWebAppUser } from "./maxWebApp";
import { applyMaxDocumentLock } from "./maxScrollGuard";
import { decodeQrStartParam } from "@mvk/shared";

export type HapticType = "light" | "medium" | "heavy" | "success" | "error" | "warning";

const DEV_STUB = import.meta.env.VITE_MAX_DEV_STUB === "true";

function webApp() {
  if (typeof window === "undefined") return undefined;
  return window.WebApp;
}

function devInitData(): string {
  const authDate = Math.floor(Date.now() / 1000);
  const user = JSON.stringify({
    id: 1,
    first_name: "Андрей",
    last_name: "Смирнов",
    username: "andrey_s",
    language_code: "ru",
  });
  return `user=${encodeURIComponent(user)}&auth_date=${authDate}&query_id=mvk-dev&hash=dev`;
}

function devInitDataUnsafe(): MaxWebAppInitDataUnsafe {
  return {
    query_id: "mvk-dev",
    auth_date: Math.floor(Date.now() / 1000),
    hash: "dev",
    user: {
      id: 1,
      first_name: "Андрей",
      last_name: "Смирнов",
      username: "andrey_s",
      language_code: "ru",
    },
    start_param: "evt-1",
  };
}

function parseEventIdFromStartParam(startParam?: string): string | null {
  if (!startParam?.trim()) return null;
  const raw = startParam.trim();
  if (/^evt-[\w-]+$/i.test(raw)) return raw;
  const fromQuery = new URLSearchParams(raw).get("event");
  if (fromQuery) return fromQuery;
  const kv = raw.match(/(?:^|&)event=([^&]+)/);
  return kv?.[1] ? decodeURIComponent(kv[1]) : null;
}

export const maxPlatform = {
  /** Running inside MAX client (WebApp object with initData). */
  isAvailable(): boolean {
    if (DEV_STUB) return true;
    const app = webApp();
    return Boolean(app && (app.initData?.length > 0 || app.initDataUnsafe?.user));
  },

  getInitData(): string {
    const fromSdk = webApp()?.initData?.trim();
    if (fromSdk) return fromSdk;
    if (DEV_STUB) return devInitData();
    return "";
  },

  getStartParam(): string | undefined {
    return webApp()?.initDataUnsafe?.start_param ?? (DEV_STUB ? "evt-1" : undefined);
  },

  resolveEventIdFromStartParam(): string | null {
    return parseEventIdFromStartParam(this.getStartParam());
  },

  /** QR opaque code from MAX start_param (`g.…` base64url). */
  resolveQrOpaqueFromStartParam(): string | null {
    const sp = this.getStartParam();
    if (!sp) return null;
    return decodeQrStartParam(sp);
  },

  getPlatform(): string {
    return webApp()?.platform ?? (DEV_STUB ? "web" : "browser");
  },

  getUser(): MaxWebAppUser | undefined {
    return webApp()?.initDataUnsafe?.user ?? (DEV_STUB ? devInitDataUnsafe().user : undefined);
  },

  ready() {
    webApp()?.ready?.();
  },

  /**
   * Ask MAX to present the mini-app in full available viewport.
   * Official Bridge has no dedicated fullscreen API — client expands the sheet
   * on `WebAppReady`. We also lock vertical swipe-dismiss and bind CSS vars
   * from getViewportSize() / visualViewport.
   * @see https://dev.max.ru/docs/webapps/bridge
   */
  async enterFullscreen() {
    const app = webApp();
    const inMax =
      DEV_STUB ||
      Boolean(app && (app.initData?.length > 0 || app.initDataUnsafe?.user));

    // Plain browser / Edge DevTools: MAX bridge is a stub — do not call it
    // (transport unavailable → uncaught promise spam, no fullscreen anyway).
    if (!inMax) {
      this.syncVisualViewportCss();
      return;
    }

    applyMaxDocumentLock();

    if (app) {
      try {
        app.ready?.();
      } catch {
        /* ignore */
      }
      // Stub bridge often rejects with a plain object (uncaught if we only try/catch).
      void Promise.resolve(app.disableVerticalSwipes?.()).catch(() => undefined);
      // Confirm before host closes the sheet (edge / overscroll gestures).
      try {
        app.enableClosingConfirmation?.();
      } catch {
        /* ignore */
      }
      try {
        app.expand?.();
      } catch {
        /* ignore */
      }
      try {
        (app as MaxWebApp & { requestFullscreen?: () => void }).requestFullscreen?.();
      } catch {
        /* ignore */
      }
      try {
        const size = await app.getViewportSize?.();
        if (size?.height) {
          const h =
            typeof size.height === "number"
              ? `${Math.round(size.height)}px`
              : String(size.height).endsWith("px")
                ? String(size.height)
                : `${size.height}px`;
          document.documentElement.style.setProperty("--max-viewport-height", h);
          document.documentElement.style.setProperty("--tg-viewport-height", h);
        }
        if (size?.width) {
          const w =
            typeof size.width === "number"
              ? `${Math.round(size.width)}px`
              : String(size.width).endsWith("px")
                ? String(size.width)
                : `${size.width}px`;
          document.documentElement.style.setProperty("--max-viewport-width", w);
        }
      } catch {
        /* fall through to visualViewport */
      }
    }

    const vv = typeof window !== "undefined" ? window.visualViewport : undefined;
    if (vv?.height) {
      this.syncVisualViewportCss();
    }
  },

  /** Map / immersive screens: re-lock host gestures so pan does not dismiss the sheet. */
  lockHostGesturesForMap() {
    const app = webApp();
    if (!app || !this.isAvailable()) return;
    void Promise.resolve(app.disableVerticalSwipes?.()).catch(() => undefined);
    try {
      app.enableClosingConfirmation?.();
    } catch {
      /* ignore */
    }
    try {
      app.expand?.();
    } catch {
      /* ignore */
    }
    try {
      (app as MaxWebApp & { requestFullscreen?: () => void }).requestFullscreen?.();
    } catch {
      /* ignore */
    }
  },

  /**
   * Keep --max-viewport-height / fixed body aligned with visualViewport.
   * Critical for iOS MAX: keyboard shrinks vv but layout viewport stays tall → white gap
   * if we keep calling enterFullscreen() on every vv resize.
   *
   * Plain PWA / Safari: do NOT pin height to visualViewport — that leaves a white gap
   * under a clipped shell (quick actions / tab bar cut in half on iPhone).
   */
  syncVisualViewportCss() {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv?.height) return;

    const h = Math.round(vv.height);
    const w = Math.round(vv.width);
    const offsetTop = Math.round(vv.offsetTop || 0);
    const inset = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
    const keyboardOpen = inset > 40;
    const isMax = document.documentElement.classList.contains("max-webapp");

    if (isMax) {
      document.documentElement.style.setProperty("--max-viewport-height", `${h}px`);
      document.documentElement.style.setProperty("--tg-viewport-height", `${h}px`);
      document.documentElement.style.setProperty("--max-viewport-width", `${w}px`);
    } else {
      document.documentElement.style.removeProperty("--max-viewport-height");
      document.documentElement.style.removeProperty("--tg-viewport-height");
      document.documentElement.style.removeProperty("--max-viewport-width");
    }

    document.documentElement.style.setProperty(
      "--keyboard-inset",
      `${keyboardOpen ? Math.round(inset) : 0}px`,
    );
    document.documentElement.style.setProperty("--vv-offset-top", `${offsetTop}px`);
    document.documentElement.classList.toggle("keyboard-open", keyboardOpen);

    const body = document.body;
    if (body?.classList.contains("max-webapp")) {
      body.style.top = `${offsetTop}px`;
      body.style.height = `${h}px`;
    }
  },

  /** Re-request fullscreen/viewport on late Bridge attach / sheet expand. */
  bootFullscreen() {
    void this.enterFullscreen();
    for (const ms of [50, 200, 500, 1200]) {
      window.setTimeout(() => void this.enterFullscreen(), ms);
    }
  },

  /** @deprecated use enterFullscreen — expand() is not in MAX Bridge */
  expand() {
    void this.enterFullscreen();
  },

  close() {
    webApp()?.close?.();
  },

  hapticFeedback(type: HapticType) {
    const haptic = webApp()?.HapticFeedback;
    if (!haptic) return;

    if (type === "success" || type === "error" || type === "warning") {
      void haptic.notificationOccurred?.(type);
      return;
    }

    const impact: "light" | "medium" | "heavy" =
      type === "heavy" ? "heavy" : type === "medium" ? "medium" : "light";
    void haptic.impactOccurred?.(impact);
  },

  showConfirm(message: string): Promise<boolean> {
    const app = webApp() as MaxWebApp | undefined;
    // Telegram-style API if the host exposes it.
    const native = (app as { showConfirm?: (msg: string, cb: (ok: boolean) => void) => void })
      ?.showConfirm;
    if (typeof native === "function") {
      return new Promise((resolve) => {
        try {
          native(message, (ok) => resolve(Boolean(ok)));
        } catch {
          resolve(true);
        }
      });
    }
    // MAX WebView: window.confirm is broken / blocked — treat as confirmed
    // (caller already got an explicit user tap, e.g. «Выйти»).
    if (this.isAvailable()) {
      return Promise.resolve(true);
    }
    try {
      return Promise.resolve(window.confirm(message));
    } catch {
      return Promise.resolve(true);
    }
  },

  showAlert(message: string): void {
    const app = webApp() as MaxWebApp | undefined;
    const native = (app as { showAlert?: (msg: string, cb?: () => void) => void })?.showAlert;
    if (typeof native === "function") {
      try {
        native(message);
        return;
      } catch {
        /* fall through */
      }
    }
    if (this.isAvailable()) {
      // Avoid blocked window.alert inside MAX.
      console.info("[maxPlatform.showAlert]", message);
      return;
    }
    try {
      window.alert(message);
    } catch {
      /* ignore */
    }
  },

  canScanQr(): boolean {
    // Browser loads max-web-app.js stub with openCodeReader — only real MAX mini-app.
    if (!this.isAvailable()) return false;
    return typeof webApp()?.openCodeReader === "function";
  },

  /**
   * Start MAX host QR reader in the *same* user-gesture turn.
   * Call synchronously from click (do not await anything before this).
   * @param fileSelect true = camera + gallery (Bridge default); false = camera only
   */
  beginCodeReader(fileSelect = false): Promise<unknown> | null {
    const app = webApp();
    const reader = app?.openCodeReader;
    if (!reader) return null;
    try {
      app.ready?.();
    } catch {
      /* ignore */
    }
    try {
      return Promise.resolve(reader.call(app, fileSelect));
    } catch {
      return null;
    }
  },

  parseCodeReaderResult(result: unknown): string | null {
    if (typeof result === "string") return result.trim() || null;
    if (!result || typeof result !== "object") return null;
    const o = result as Record<string, unknown>;
    const v = o.value ?? o.data ?? o.result ?? o.code ?? o.text;
    if (typeof v === "string") return v.trim() || null;
    return null;
  },

  isCodeReaderCancel(err: unknown): boolean {
    const msg = err instanceof Error ? err.message : String(err ?? "");
    const code =
      err && typeof err === "object"
        ? String((err as { code?: string; error?: { code?: string } }).code ??
            (err as { error?: { code?: string } }).error?.code ??
            "")
        : "";
    return /cancel|closed|abort|dismiss|user_refused|refused|denied/i.test(`${msg} ${code}`);
  },

  async scanQrCode(cameraOnly = true): Promise<string | null> {
    // Prefer sync begin from click handlers; this helper is for non-gesture callers.
    const fileSelect = !cameraOnly;
    let pending = this.beginCodeReader(fileSelect);
    if (!pending && cameraOnly) {
      // Some MAX builds reject camera-only — fall back to default (camera + file).
      pending = this.beginCodeReader(true);
    }
    if (!pending) return null;
    try {
      return this.parseCodeReaderResult(await pending);
    } catch (err) {
      if (this.isCodeReaderCancel(err)) return null;
      if (cameraOnly) {
        try {
          const retry = this.beginCodeReader(true);
          if (retry) return this.parseCodeReaderResult(await retry);
        } catch {
          /* ignore */
        }
      }
      return null;
    }
  },

  async shareText(text: string, link?: string): Promise<boolean> {
    const app = webApp();
    const payload = { text, link };
    try {
      if (app?.shareMaxContent) {
        await app.shareMaxContent(payload);
        return true;
      }
      if (app?.shareContent) {
        await app.shareContent(payload);
        return true;
      }
      if (navigator.share) {
        await navigator.share({ text, url: link });
        return true;
      }
    } catch {
      return false;
    }
    return false;
  },

  openExternalLink(url: string) {
    const app = webApp();
    if (app?.openLink) {
      app.openLink(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  },

  /** Open https://max.ru/… inside MAX (bot chat / startapp). Falls back to openLink. */
  openMaxLink(url: string) {
    const app = webApp();
    if (app?.openMaxLink) {
      app.openMaxLink(url);
      return;
    }
    this.openExternalLink(url);
  },

  get safeAreaInset() {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  },

  get colorScheme(): "light" | "dark" {
    return "light";
  },

  get initDataUnsafe() {
    return webApp()?.initDataUnsafe ?? (DEV_STUB ? devInitDataUnsafe() : {});
  },

  canRequestContact(): boolean {
    return typeof webApp()?.requestContact === "function";
  },

  async requestContact(): Promise<{ phone: string; authDate: string; hash: string }> {
    const app = webApp();
    const fn = app?.requestContact;
    if (!fn) throw new Error("request_contact_unavailable");

    try {
      app?.ready?.();
    } catch {
      // ignore
    }

    const pickContactFields = (raw: Record<string, unknown>) => {
      const nested =
        (raw.contact as Record<string, unknown> | undefined) ||
        (raw.data as Record<string, unknown> | undefined) ||
        (raw.response as Record<string, unknown> | undefined) ||
        raw;
      const phone = String(
        nested.phone ?? nested.phone_number ?? nested.phoneNumber ?? raw.phone ?? raw.phone_number ?? "",
      ).trim();
      const authDate = String(
        nested.authDate ?? nested.auth_date ?? raw.authDate ?? raw.auth_date ?? "",
      ).trim();
      const hash = String(nested.hash ?? raw.hash ?? "").trim();
      return { phone, authDate, hash, nested };
    };

    const classifyError = (payload: unknown): Error => {
      const obj = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : null;
      const errObj =
        (obj?.error as { code?: string; reason?: string; message?: string } | undefined) ||
        (obj as { code?: string; reason?: string; message?: string } | null);
      const code = String(errObj?.code ?? "");
      const reason = String(errObj?.reason ?? errObj?.message ?? "");
      const blob = `${code} ${reason}`.toLowerCase();
      if (blob.includes("user_refused") || blob.includes("refused")) {
        return new Error("request_contact_refused");
      }
      if (blob.includes("request_error")) {
        return new Error("request_contact_error");
      }
      if (code || reason) {
        return new Error(`request_contact_error:${code || reason}`);
      }
      if (payload instanceof Error) {
        return new Error(payload.message || "request_contact_failed");
      }
      return new Error("request_contact_failed");
    };

    let raw: Record<string, unknown>;
    try {
      const result = await Promise.resolve(fn.call(app));
      if (result == null || typeof result !== "object") {
        throw new Error("request_contact_empty");
      }
      raw = result as Record<string, unknown>;
    } catch (err) {
      throw classifyError(err);
    }

    const nestedError = raw.error as { code?: string; reason?: string } | undefined;
    if (nestedError?.code || nestedError?.reason) {
      throw classifyError(raw);
    }

    const { phone, authDate, hash } = pickContactFields(raw);
    if (!phone) {
      const err = new Error("request_contact_incomplete");
      (err as Error & { raw?: unknown }).raw = {
        keys: Object.keys(raw),
        phoneLen: 0,
        authDateLen: authDate.length,
        hashLen: hash.length,
      };
      throw err;
    }
    // iOS MAX often returns only { phone } — proceed; API accepts unverified contact.
    if (!authDate || !hash) {
      console.warn("[max] requestContact phone-only (no hash)", {
        phoneLen: phone.length,
        keys: Object.keys(raw),
      });
    }
    return { phone, authDate: authDate || "", hash: hash || "" };
  },
};
